<template>
  <button
    ref="button"
    :aria-controls="`${PLAY_SLIP_EDITOR_ID}-tabpanel-${playIndex}`"
    :aria-label="$t('common.carousel.button.tab', { slide: playNumber })"
    :aria-selected="isActive"
    class="lottery-pagination-item"
    :class="{ active: isActive }"
    role="tab"
    :tabindex="isActive ? 0 : -1"
    type="button"
  >
    <strong class="flex-1 caption-bold text-center">
      {{ playNumber }}
    </strong>

    <LcIcon
      v-if="play.isDirty"
      class="icon"
      :class="{ 'icon--complete': play.isComplete }"
      :name="play.isComplete ? 'checkmark' : 'close'"
      size="small"
    />
  </button>
</template>

<script lang="ts" setup>
import type { PlaySlipPlay } from '../composables/playSlip'

import { PLAY_SLIP_EDITOR_ID } from '../constants/playSlip'

type LotteryPaginationItemProps = {
  firstIncompletePlayId?: string
  play: PlaySlipPlay
  playIndex: number
  plays: PlaySlipPlay[]
  visiblePlayId?: string
}

const { play, playIndex, plays, visiblePlayId } = defineProps<LotteryPaginationItemProps>()

const buttonRef = useTemplateRef<HTMLElement>('button')

const isActive = computed(() => play.id === visiblePlayId)
const playNumber = computed(() => playIndex + 1)

const prefersReducedMotion = useReducedMotion()

const scrollIntoView = async (behavior: ScrollBehavior = 'smooth') => {
  if (!isActive.value) return

  await waitMs(50)
  buttonRef.value?.scrollIntoView({
    behavior: prefersReducedMotion.value ? 'instant' : behavior,
    block: 'nearest',
    inline: 'center',
  })
}

watch(isActive, (isActiveNow) => {
  if (!isActiveNow) return
  scrollIntoView('smooth')
})

watch(
  () => plays.length,
  (newLength, oldLength) => {
    if (newLength <= oldLength) return
    scrollIntoView()
  },
)

defineExpose({ focus: () => buttonRef.value?.focus({ preventScroll: true }), scrollIntoView })
</script>

<style lang="scss" scoped>
.lottery-pagination-item {
  position: relative;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 3.5rem;
  height: 1.5rem;
  padding: token('size/space/gap/050') token('size/space/gap/100');
  border: token('size/border/100') solid token('color/border/primary/primary');
  border-radius: token('size/radius/l');
  background-color: token('color/background/accent/info/info/extralight');
  color: token('color/text/primary/primary');
  cursor: pointer;

  @include hover-state {
    border-color: token('color/lightblue/700');
    box-shadow: 0 0 0 token('size/border/200') token('color/background/accent/info/info/extralight');
  }

  @include focus-state {
    &::before {
      inset: 0;
      border-radius: 0.75rem;
      box-shadow: 0 0 0 token('size/border/200') token('color/border/accent/focused/focused');
    }
  }

  .icon {
    color: token('color/icon/accent/prominent/prominent');

    &--complete {
      color: token('color/icon/accent/success/success');
    }
  }

  &.active {
    border-color: token('color/background/accent/info/info-bold');
    background-color: token('color/background/accent/info/info-bold');
    box-shadow: token('shadow/shadow/card/small');
    color: token('color/text/primary/primary');
    cursor: default;

    > .icon {
      color: token('color/text/primary/primary');
    }
  }
}
</style>
